import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ShipmentTransportOrderOuterPackagesService } from './shipment-transport-order-outer-packages.service';
import { CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MeasurementUnitCodesEnum, UnitSystemListModel } from '../../../../../common/models/unit-system.model';
import { ListClientPaginationStateModel, NotificationService } from '../../../../../common';
import {
  ShipmentTransportOrderOuterPackagesListInitialState,
  ShipmentTransportOrderOuterPackagesListLoad,
} from './shipment-transport-order-outer-packages.actions';
import {
  ShipmentTransportOrderOuterPackagesModel,
  ShipmentTransportOrderOuterPackagesResponseModel,
} from '../../models/shipment-transport-order-outer-packages.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type ShipmentTransportOrderOuterPackagesListStateModel =
  ListClientPaginationStateModel<ShipmentTransportOrderOuterPackagesModel> & UnitSystemListModel;

const initialState: ShipmentTransportOrderOuterPackagesListStateModel = {
  items: [],
  dimensionsUnitCode: null,
  weightUnitCode: null,
  volumeUnitCode: null,
  loaded: false,
};

@State<ShipmentTransportOrderOuterPackagesListStateModel>({
  name: 'shipmentTransportOrderOuterPackages',
  defaults: initialState,
})
@Injectable()
export class ShipmentTransportOrderOuterPackagesState {
  constructor(
    public transportOrderOuterPackagesService: ShipmentTransportOrderOuterPackagesService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(
    state: ShipmentTransportOrderOuterPackagesListStateModel,
  ): ShipmentTransportOrderOuterPackagesListStateModel {
    return state;
  }

  @Selector()
  static getDimensionsUnitCode(state: ShipmentTransportOrderOuterPackagesListStateModel): MeasurementUnitCodesEnum {
    return state.dimensionsUnitCode;
  }

  @Selector()
  static getWeightUnitCode(state: ShipmentTransportOrderOuterPackagesListStateModel): MeasurementUnitCodesEnum {
    return state.weightUnitCode;
  }

  @Selector()
  static getVolumeUnitCode(state: ShipmentTransportOrderOuterPackagesListStateModel): MeasurementUnitCodesEnum {
    return state.volumeUnitCode;
  }

  @Selector()
  static getLoaded(state: ShipmentTransportOrderOuterPackagesListStateModel): boolean {
    return state.loaded;
  }

  @Action(ShipmentTransportOrderOuterPackagesListLoad)
  getTransportOrderOuterPackagesList(
    ctx: StateContext<ShipmentTransportOrderOuterPackagesListStateModel>,
    { payload }: ShipmentTransportOrderOuterPackagesListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportOrderOuterPackagesService.getOuterPackages(payload).pipe(
      map((response: ShipmentTransportOrderOuterPackagesResponseModel) => {
        const items = response.items.map(
          (item: ShipmentTransportOrderOuterPackagesModel) => new ShipmentTransportOrderOuterPackagesModel(item),
        );
        ctx.patchState({
          items,
          dimensionsUnitCode: response.dimensionsUnitCode,
          weightUnitCode: response.weightUnitCode,
          volumeUnitCode: response.volumeUnitCode,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentTransportOrderOuterPackagesListInitialState)
  setInitialState(ctx: StateContext<ShipmentTransportOrderOuterPackagesListStateModel>): void {
    ctx.setState(initialState);
  }
}
